import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { fetchPageBySlug } from '../lib/cms'
import useMeta from '../hooks/useMeta'
import { Phone, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react'

export default function YdelseDetail() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      setLoading(true)
      // Map route slug to page_slug in CMS
      const data = await fetchPageBySlug(slug)
      if (!data) {
        // If not found, redirect to /jeg-tilbyder
        navigate('/jeg-tilbyder')
        return
      }
      setPageData(data)
      setLoading(false)
    }
    loadData()
  }, [slug, navigate])

  // Set metadata dynamically
  useMeta({
    title: pageData?.meta_title || pageData?.title || 'Ydelse · JP Stål og Rør ApS',
    description: pageData?.meta_description || 'Professionelle stål- og rørløsninger til erhvervskunder.',
    ogTitle: pageData?.meta_title || pageData?.title,
    ogDescription: pageData?.meta_description,
    ogImage: pageData?.images?.[0] || 'https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/d1a201a2-613d-4546-aa68-2fafe952f02e.jpeg',
    canonicalUrl: `https://jpstaalogroer.dk/${slug}`
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-silver-200 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  const image = pageData.images?.[0] || 'https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/d1a201a2-613d-4546-aa68-2fafe952f02e.jpeg'

  return (
    <div className="bg-silver-200 text-slate-900 min-h-screen py-16 md:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Back Link */}
        <div className="mb-8">
          <Link
            to="/jeg-tilbyder"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-700 hover:text-slate-950 transition-colors duration-200 cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Tilbage til ydelser</span>
          </Link>
        </div>

        {/* Article Layout */}
        <article className="bg-silver-100 border border-silver-400 rounded-sm overflow-hidden shadow-2xl">
          
          {/* Main Image */}
          <div className="aspect-[21/9] bg-silver-300 relative overflow-hidden">
            <img
              src={image}
              alt={pageData.title}
              className="w-full h-full object-cover filter grayscale"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-silver-100 via-transparent to-transparent" />
          </div>

          {/* Content */}
          <div className="p-8 md:p-12 space-y-8">
            
            {/* Render HTML content verbatim from CMS */}
            <div
              className="prose max-w-none text-slate-800 leading-relaxed space-y-6 text-sm
                prose-headings:text-slate-950 prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tight
                prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-h4:text-lg
                prose-p:leading-relaxed prose-p:mb-4
                prose-ul:list-disc prose-ul:pl-5 prose-ul:space-y-2 prose-ul:mb-4
                prose-li:text-slate-800
                prose-a:text-amber-600 prose-a:font-bold hover:prose-a:text-amber-700 prose-a:transition-colors"
              dangerouslySetInnerHTML={{ __html: pageData.content }}
            />

            {/* Heavy CTA Buttons */}
            <div className="border-t border-silver-300 pt-8 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <p className="text-sm font-bold text-slate-950 uppercase tracking-wider">Har du brug for hjælp til dette?</p>
                <p className="text-xs text-slate-600 mt-1">Kontakt os i dag for en uforpligtende snak eller et prisoverslag.</p>
              </div>
              <div className="flex flex-wrap gap-3 shrink-0 w-full sm:w-auto">
                <a
                  href="tel:+4524460485"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black uppercase tracking-wider text-xs px-6 py-4 rounded-sm transition-colors duration-200 cursor-pointer shadow-md"
                >
                  <Phone className="w-4 h-4" />
                  <span>Ring: (+45) 24 46 04 85</span>
                </a>
                <a
                  href="mailto:jp@jpstaal.dk"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-silver-300 hover:bg-silver-400 text-slate-950 border border-silver-400 font-bold uppercase tracking-wider text-xs px-6 py-4 rounded-sm transition-colors duration-200 cursor-pointer shadow-sm"
                >
                  <Mail className="w-4 h-4 text-amber-600" />
                  <span>Mail: jp@jpstaal.dk</span>
                </a>
              </div>
            </div>

          </div>

        </article>

      </div>
    </div>
  )
}
